/**
 * Bilingual form labels — English always, Telugu on demand.
 *
 * The Display Devices forms label every REQUIRED field twice: the English name
 * the certificate and the statute use, and a Telugu line under it for the
 * applicant. Not everyone wants the second line, so it is switchable — and the
 * switch has to be readable from two different packages (the licence fields
 * live in `@maud/erp-display-devices`, the device wizard in `@maud/ds-vendor`)
 * without threading a provider through both.
 *
 * Hence a tiny external store rather than React context: `useSyncExternalStore`
 * over a module-level flag, mirrored into localStorage so the choice survives a
 * reload and follows the applicant from the licence form into the device
 * wizard. A `storage` listener keeps duplicate tabs in step.
 *
 * Default is ON. A vendor who needs Telugu should not have to discover a
 * setting to get it; a vendor who does not can turn it off once and be done.
 */
import { useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

const KEY = 'ds.formLang.telugu';
const EVENT = 'ds:form-lang-change';

/** localStorage throws outright in some privacy modes — never let that break a form. */
const read = (): boolean => {
  try {
    return localStorage.getItem(KEY) !== '0';
  } catch {
    return true;
  }
};

// getSnapshot must return a stable value between changes, so the flag is cached
// here rather than re-read from localStorage on every render.
let cached = read();

const subscribe = (onChange: () => void): (() => void) => {
  const handler = () => {
    cached = read();
    onChange();
  };
  window.addEventListener(EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener('storage', handler);
  };
};

/** True when Telugu sub-labels should render. */
export const useShowTelugu = (): boolean =>
  useSyncExternalStore(subscribe, () => cached, () => true);

export const setShowTelugu = (on: boolean): void => {
  cached = on;
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    // Unwritable storage only costs persistence — the toggle still works for
    // this session, which is the part the applicant actually notices.
  }
  window.dispatchEvent(new Event(EVENT));
};

/**
 * Picks the Telugu string when the switch is on, the English one otherwise.
 *
 * `Te` stacks a second line under a label; this REPLACES the text instead —
 * which is what placeholders, hints and section subtitles need, since none of
 * them has room to say the same thing twice.
 */
export const useT = (): ((en: string, te: string) => string) => {
  const show = useShowTelugu();
  return (en, te) => (show ? te : en);
};

/**
 * Telugu sub-label for a required field.
 *
 * Renders nothing when the toggle is off, so the form collapses back to its
 * single-line English layout rather than leaving blank rows behind. `lang` is
 * set so a screen reader switches voice instead of reading Telugu with English
 * phonetics.
 */
export const Te = ({ children }: { children: ReactNode }) => {
  const show = useShowTelugu();
  if (!show) return null;
  return (
    <span lang="te" className="mt-0.5 block text-[11px] font-normal leading-tight text-slate-500 dark:text-slate-400">
      {children}
    </span>
  );
};

/**
 * The EN / తెలుగు segmented switch.
 *
 * `tone="dark"` is for the device wizard, whose header is a colored gradient
 * with white text; the default suits a white card. Each half is a real button
 * with `aria-pressed`, so the control is one tab stop per option and announces
 * its own state — a styled div with a click handler would announce neither.
 */
export const LanguageToggle = ({ tone = 'light', className = '' }: {
  tone?: 'light' | 'dark';
  className?: string;
}) => {
  const show = useShowTelugu();

  const base = 'rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2';
  const wrap = tone === 'dark'
    ? 'inline-flex items-center gap-0.5 rounded-lg bg-white/10 p-0.5 ring-1 ring-white/20'
    : 'inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 ring-1 ring-slate-200';
  const on = tone === 'dark'
    ? 'bg-white text-slate-900 shadow-sm focus-visible:ring-white'
    : 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200 focus-visible:ring-indigo-500';
  const off = tone === 'dark'
    ? 'text-white/80 hover:text-white focus-visible:ring-white'
    : 'text-slate-500 hover:text-slate-800 focus-visible:ring-indigo-500';

  return (
    <div role="group" aria-label="Field label language" className={`${wrap} ${className}`}>
      <button
        type="button"
        aria-pressed={!show}
        onClick={() => setShowTelugu(false)}
        title="Show field labels in English only"
        className={`${base} ${!show ? on : off}`}
      >
        English
      </button>
      <button
        type="button"
        aria-pressed={show}
        onClick={() => setShowTelugu(true)}
        title="Show a Telugu line under each required field"
        lang="te"
        className={`${base} ${show ? on : off}`}
      >
        తెలుగు
      </button>
    </div>
  );
};

export default LanguageToggle;
